/**
 * TherapistLiteWeekly — public, token-only page.
 *
 * The therapist arrives from their weekly PlayFirst email and can set
 * up to 3 goals per linked child, then save. Explicitly designed to be
 * usable in ~2 minutes on a phone.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Save, Check, Sparkles } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;
const MAX_GOALS = 3;

function ChildBlock({ child, goalsDraft, onGoalChange }) {
  const goals = goalsDraft[child.child_id] ?? child.current_goals ?? [];
  return (
    <Card className="mb-4" data-testid={`tl-child-card-${child.child_id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-lg">{child.child_name}</CardTitle>
            <p className="text-sm text-stone-500 mt-1">
              {child.parent_name ? `Family: ${child.parent_name}` : child.parent_email}
              {child.age_years != null ? ` · ${child.age_years} yrs` : ''}
              {child.communication_style ? ` · ${child.communication_style}` : ''}
            </p>
          </div>
          {(child.current_goals || []).length > 0 && (
            <Badge variant="outline" className="text-xs">
              Last set {child.goals_updated_at ? new Date(child.goals_updated_at).toLocaleDateString() : '—'}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {[0, 1, 2].map((i) => (
          <Input
            key={i}
            value={goals[i] || ''}
            placeholder={`Goal ${i + 1}${i === 0 ? '  — e.g. "Use 2-word phrases when requesting"' : ''}`}
            onChange={(e) => onGoalChange(child.child_id, i, e.target.value)}
            data-testid={`tl-goal-input-${child.child_id}-${i}`}
            maxLength={240}
          />
        ))}
      </CardContent>
    </Card>
  );
}

export default function TherapistLiteWeekly() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [goalsDraft, setGoalsDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BASE}/api/therapist-lite/weekly/${token}`);
        setState({ loading: false, error: '', data });
        // Prime draft with current goals so therapist tweaks rather than starts blank.
        const draft = {};
        (data.children || []).forEach((c) => {
          draft[c.child_id] = [...(c.current_goals || [])];
        });
        setGoalsDraft(draft);
        if (data.used_at) setSavedAt(data.used_at);
      } catch (e) {
        setState({
          loading: false,
          error: e?.response?.data?.detail || 'Link not recognised.',
          data: null,
        });
      }
    })();
  }, [token]);

  const handleGoalChange = (childId, idx, value) => {
    setGoalsDraft((prev) => {
      const arr = [...(prev[childId] || [])];
      arr[idx] = value;
      return { ...prev, [childId]: arr };
    });
  };

  const nonEmptyByChild = useMemo(() => {
    const out = {};
    Object.entries(goalsDraft).forEach(([cid, list]) => {
      out[cid] = (list || []).map((s) => (s || '').trim()).filter(Boolean).slice(0, MAX_GOALS);
    });
    return out;
  }, [goalsDraft]);

  const totalGoals = Object.values(nonEmptyByChild).reduce((a, b) => a + b.length, 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${BASE}/api/therapist-lite/weekly/${token}`,
        { goals_by_child: nonEmptyByChild },
      );
      setSavedAt(data.used_at);
      toast.success(
        `Saved ${totalGoals} goal${totalGoals === 1 ? '' : 's'} across ${data.saved.length} child${data.saved.length === 1 ? '' : 'ren'}.`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50" data-testid="tl-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#7C6F9C]" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6" data-testid="tl-error">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-stone-800">Link unavailable</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-stone-600">{state.error}</p>
            <p className="text-sm text-stone-500 mt-3">
              You'll receive a fresh link on Monday morning — no action needed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { therapist_name, week_start, children } = state.data;
  const weekLabel = week_start
    ? new Date(week_start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '';

  return (
    <div className="min-h-screen bg-stone-50 py-8 px-4" data-testid="tl-page">
      <div className="max-w-2xl mx-auto">
        <header className="mb-6">
          <div className="flex items-center gap-2 text-[#7C6F9C] mb-2">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-wide">PlayFirst — Weekly Goals</span>
          </div>
          <h1 className="text-2xl font-bold text-stone-800">
            Hi {therapist_name || 'there'} — set this week's goals
          </h1>
          <p className="text-sm text-stone-600 mt-2 leading-relaxed">
            Week of <strong>{weekLabel}</strong>. Set up to <strong>3 goals per child</strong>. Ideas the parent gets each
            day on WhatsApp will gently target them. If you don't change anything, last week's goals stay in place.
          </p>
          {savedAt && (
            <p className="text-sm text-green-700 mt-3 flex items-center gap-1.5" data-testid="tl-saved-indicator">
              <Check className="w-4 h-4" />
              Saved on {new Date(savedAt).toLocaleString()}
            </p>
          )}
        </header>

        {(!children || children.length === 0) ? (
          <Card>
            <CardContent className="py-8 text-center text-stone-600">
              No linked families yet. Once a parent invites you, they'll show up here every Monday.
            </CardContent>
          </Card>
        ) : (
          <>
            {children.map((c) => (
              <ChildBlock
                key={c.child_id}
                child={c}
                goalsDraft={goalsDraft}
                onGoalChange={handleGoalChange}
              />
            ))}

            <div className="sticky bottom-4 bg-white rounded-2xl shadow-lg border border-stone-200 p-4 flex items-center justify-between gap-3">
              <div className="text-sm text-stone-600">
                <strong>{totalGoals}</strong> goal{totalGoals === 1 ? '' : 's'} across {children.length} child
                {children.length === 1 ? '' : 'ren'}
              </div>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#7C6F9C] hover:bg-[#6a5f8a]"
                data-testid="tl-save-button"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save this week's goals
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
